import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Filter, Trash2, Shield } from "lucide-react";
import KiviHealthBridge from "@/components/KiviHealthBridge";
import VoiceInput from "@/components/VoiceInput";

const NUMERIC_FIELDS = new Set(["age", "mobile", "alt_mobile", "relative_mobile", "pincode", "emergency_contact"]);
const FIELD_MAX = { age: 3, mobile: 10, alt_mobile: 10, relative_mobile: 10, pincode: 6, emergency_contact: 10 };

const empty = { name: "", age: "", gender: "Male", mobile: "", relative_name: "", relative_mobile: "", blood_group: "", address: "", city: "Jamnagar", state: "Gujarat", pincode: "", pmjay: false };
const FIELDS = [
  ["name", "Full Name"], ["age", "Age"], ["gender", "Gender"], ["mobile", "Mobile"],
  ["alt_mobile", "Alt Mobile"], ["relative_name", "Relative Name"], ["relative_mobile", "Relative Mobile"],
  ["blood_group", "Blood Group"], ["marital_status", "Marital Status"], ["city", "City"],
  ["state", "State"], ["pincode", "Pincode"], ["emergency_contact", "Emergency Contact"], ["reference_doctor", "Reference Doctor"],
  ["address", "Address"],
];

export default function Patients() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(sp.get("q") || "");
  const [gender, setGender] = useState("");
  const [blood, setBlood] = useState("");
  const [pmjayF, setPmjayF] = useState("");
  const [open, setOpen] = useState(sp.get("new") === "1");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  // React to shortcut re-navigation (Ctrl+R when already on /patients).
  useEffect(() => {
    if (sp.get("new") === "1") {
      const prefill = sp.get("prefill") || "";
      // If prefill looks like digits it's likely a mobile; otherwise, treat as name
      const isPhone = /^\d{6,}$/.test(prefill);
      setForm({ ...empty, ...(prefill ? (isPhone ? { mobile: prefill } : { name: prefill }) : {}) });
      setEditing(null);
      setOpen(true);
    }
  }, [sp]);

  // Every logged-in user can edit patient details (including corrections to
  // name / age / mobile).  Only admin / super_admin can delete a patient;
  // super_admin gets a force-cascade delete.
  const canEdit = !!user;
  const canDelete = ["admin", "super_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const [kiviFor, setKiviFor] = useState(null);   // patient to bridge to KiviHealth

  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const load = () => api.get("/patients", { params: { q, limit, skip: page * limit } }).then((r) => {
    setRows(r.data);
    // Total exposed via X-Total-Count header
    const t = parseInt(r.headers?.["x-total-count"] || r.headers?.["X-Total-Count"] || "0", 10);
    setTotal(Number.isFinite(t) && t > 0 ? t : r.data.length);
  });
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [q, limit]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, limit, page]);

  const filtered = rows.filter((p) =>
    (!gender || p.gender === gender) &&
    (!blood || p.blood_group === blood) &&
    (pmjayF === "" || String(p.pmjay) === pmjayF)
  );

  const pageCount = Math.max(1, Math.ceil(total / limit));

  const save = async () => {
    if (!form.name || !form.mobile || !form.age) return toast.error("Name, age, mobile required");
    try {
      const payload = { ...form, age: parseInt(form.age) };
      if (editing) {
        await api.put(`/patients/${editing.id}`, payload);
        toast.success("Patient updated");
      } else {
        await api.post("/patients", payload);
        toast.success("Patient registered");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (p, e) => {
    e.stopPropagation();
    setEditing(p); setForm({ ...empty, ...p }); setOpen(true);
  };

  const removePatient = async (p, e) => {
    e.stopPropagation();
    const forceHint = isSuper ? " Super Admin will also wipe every OPD/IPD/bill for this patient." : "";
    if (!window.confirm(`Delete patient ${p.name} (${p.uhid})?${forceHint}\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/patients/${p.id}`, { params: isSuper ? { force: true } : {} });
      toast.success("Patient deleted");
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Delete failed";
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Patients</h1>
          <p className="text-sm text-slate-500">Master registry of all registered patients.</p>
        </div>
        <Button data-testid="add-patient-btn" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-blue-700 hover:bg-blue-800 rounded-sm">+ Register Patient</Button>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="p-3 border-b border-slate-200 flex gap-2 flex-wrap items-center">
          <Input data-testid="patient-search" placeholder="Search by name / UHID / mobile" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9 border-slate-300" />
          <Filter className="w-4 h-4 text-slate-500 ml-2" />
          <select value={gender} onChange={(e) => setGender(e.target.value)} className="border border-slate-300 h-9 text-sm px-2"><option value="">All Gender</option><option>Male</option><option>Female</option></select>
          <select value={blood} onChange={(e) => setBlood(e.target.value)} className="border border-slate-300 h-9 text-sm px-2"><option value="">All Blood</option>{["A+","A-","B+","B-","O+","O-","AB+","AB-"].map(b => <option key={b}>{b}</option>)}</select>
          <select value={pmjayF} onChange={(e) => setPmjayF(e.target.value)} className="border border-slate-300 h-9 text-sm px-2"><option value="">All PMJAY/Non</option><option value="true">PMJAY</option><option value="false">Non-PMJAY</option></select>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-600">
            <span>Rows per page</span>
            <select data-testid="pt-page-size" value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} className="border border-slate-300 h-9 text-sm px-2">
              {[25, 50, 100, 200, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
              <option value={5000}>All</option>
            </select>
            <span className="text-slate-500">·</span>
            <span data-testid="pt-page-info">{page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total.toLocaleString("en-IN")}</span>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="label-tiny">UHID</TableHead>
              <TableHead className="label-tiny">Name</TableHead>
              <TableHead className="label-tiny">Age/Gender</TableHead>
              <TableHead className="label-tiny">Mobile</TableHead>
              <TableHead className="label-tiny">City</TableHead>
              <TableHead className="label-tiny">Blood</TableHead>
              <TableHead className="label-tiny">PMJAY</TableHead>
              <TableHead className="label-tiny w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} className="cursor-pointer hover:bg-blue-50" onClick={() => nav(`/patients/${p.id}`)} data-testid={`patient-row-${p.uhid}`}>
                <TableCell className="font-mono text-xs">{p.uhid}</TableCell>
                <TableCell className="font-medium text-slate-900">{p.name}</TableCell>
                <TableCell className="text-sm">{p.age} / {p.gender}</TableCell>
                <TableCell className="font-mono text-xs">{p.mobile}</TableCell>
                <TableCell className="text-sm">{p.city}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm">{p.blood_group || "-"}</Badge></TableCell>
                <TableCell>{p.pmjay ? <Badge className="bg-teal-100 text-teal-800 rounded-sm">PMJAY</Badge> : <span className="text-xs text-slate-400">-</span>}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {canEdit && (
                      <button data-testid={`edit-patient-${p.uhid}`} onClick={(e) => startEdit(p, e)} className="text-blue-700 hover:bg-blue-100 p-1 rounded-sm" title="Edit"><Pencil className="w-4 h-4"/></button>
                    )}
                    <button
                      data-testid={`kivi-btn-${p.uhid}`}
                      onClick={(e) => { e.stopPropagation(); setKiviFor(p); }}
                      className={`p-1 rounded-sm ${p.abha_number ? "text-teal-700 hover:bg-teal-100" : "text-slate-500 hover:bg-slate-100"}`}
                      title={p.abha_number ? `ABHA linked: ${p.abha_number}` : "Create ABHA via KiviHealth"}
                    >
                      <Shield className="w-4 h-4"/>
                    </button>
                    {canDelete && (
                      <button data-testid={`delete-patient-${p.uhid}`} onClick={(e) => removePatient(p, e)} className="text-red-600 hover:bg-red-100 p-1 rounded-sm" title="Delete"><Trash2 className="w-4 h-4"/></button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* Pagination footer */}
        <div className="p-3 border-t border-slate-200 flex items-center justify-between text-xs">
          <div className="text-slate-500">
            Page <b>{page + 1}</b> of <b>{pageCount.toLocaleString("en-IN")}</b>
            {filtered.length !== rows.length && <span className="ml-2">· {filtered.length} shown after filter</span>}
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="pt-first" disabled={page === 0} onClick={() => setPage(0)} className="px-2 h-8 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-50">« First</button>
            <button data-testid="pt-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-3 h-8 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-50">‹ Prev</button>
            {/* Numbered pages window (up to 5 buttons) */}
            {(() => {
              const w = 2;
              const start = Math.max(0, Math.min(page - w, pageCount - (2 * w + 1)));
              const end = Math.min(pageCount, start + (2 * w + 1));
              const arr = [];
              for (let i = start; i < end; i++) arr.push(i);
              return arr.map((i) => (
                <button key={i} data-testid={`pt-page-${i}`} onClick={() => setPage(i)}
                  className={`w-8 h-8 border rounded-sm ${i === page ? "bg-blue-700 text-white border-blue-700 font-semibold" : "border-slate-300 hover:bg-slate-50"}`}>{i + 1}</button>
              ));
            })()}
            <button data-testid="pt-next" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} className="px-3 h-8 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-50">Next ›</button>
            <button data-testid="pt-last" disabled={page >= pageCount - 1} onClick={() => setPage(pageCount - 1)} className="px-2 h-8 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-50">Last »</button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing ? `Edit Patient — ${editing.uhid}` : "Register New Patient"}</DialogTitle></DialogHeader>

          {/* Custom UHID row — admin only. Blank = auto-generate on save. */}
          {["admin", "super_admin"].includes(user?.role) && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-2">
              <Label className="label-tiny text-amber-800 flex items-center gap-1">
                <Shield className="w-3 h-3" /> Custom UHID {editing ? "(change propagates to all existing records)" : "(leave blank to auto-generate)"}
              </Label>
              <Input
                data-testid="pt-custom-uhid"
                value={form.uhid || ""}
                onChange={(e) => setForm({ ...form, uhid: e.target.value.toUpperCase() })}
                placeholder={editing ? editing.uhid : "e.g. DH25/001234 — leave blank for auto"}
                className="mt-1 h-9 font-mono"
              />
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            {FIELDS.map(([k, l]) => (
              <div key={k}>
                <Label className="label-tiny">{l}</Label>
                {k === "gender" ? (
                  <select className="mt-1 h-9 border-slate-300 border w-full px-2 text-sm" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}><option>Male</option><option>Female</option><option>Other</option></select>
                ) : k === "marital_status" ? (
                  <select className="mt-1 h-9 border-slate-300 border w-full px-2 text-sm" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}><option value="">--</option><option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option></select>
                ) : k === "blood_group" ? (
                  <select className="mt-1 h-9 border-slate-300 border w-full px-2 text-sm" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}><option value="">--</option>{["A+","A-","B+","B-","O+","O-","AB+","AB-"].map(b => <option key={b}>{b}</option>)}</select>
                ) : (
                  <VoiceInput
                    as="input"
                    testid={`pt-${k}`}
                    className="mt-1"
                    numeric={NUMERIC_FIELDS.has(k)}
                    maxLength={FIELD_MAX[k]}
                    value={form[k] || ""}
                    onChange={(v) => setForm({ ...form, [k]: v })}
                  />
                )}
              </div>
            ))}
            <label className="flex items-center gap-2 self-end text-sm">
              <input data-testid="pt-pmjay" type="checkbox" checked={form.pmjay} onChange={(e) => setForm({ ...form, pmjay: e.target.checked })} />
              PMJAY Beneficiary
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="pt-save" onClick={save} className="bg-blue-700 hover:bg-blue-800">{editing ? "Save Changes" : "Register Patient"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <KiviHealthBridge
        open={!!kiviFor}
        onOpenChange={(v) => { if (!v) setKiviFor(null); }}
        patient={kiviFor}
        onLinked={() => { setKiviFor(null); load(); }}
      />
    </div>
  );
}
